import { z } from 'zod';

type LanguagePriority = 'user' | 'application';

const FeatureFlagsSchema = z.object({
  enableThemeToggle: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_THEME_TOGGLE'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable theme toggle in the user interface.' }),
  enableAccountDeletion: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable personal account deletion.' }),
  enableTeamDeletion: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable team deletion.' }),
  enableTeamAccounts: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable team accounts.' }),
  enableTeamCreation: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable team creation.' }),
  enablePersonalAccountBilling: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable personal account billing.' }),
  enableTeamAccountBilling: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable team account billing.' }),
  languagePriority: z
    .enum(['user', 'application'], {
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_LANGUAGE_PRIORITY'
          : 'Not a string',
    })
    .meta({
      description: `If set to user, use the user's preferred language. If set to application, use the application's default language.`,
    })
    .default('application'),
  enableNotifications: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_NOTIFICATIONS'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable notifications functionality' }),
  realtimeNotifications: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_REALTIME_NOTIFICATIONS'
          : 'Not a boolean',
    })
    .meta({
      description: 'Enable realtime for the notifications functionality',
    }),
  enableVersionUpdater: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_VERSION_UPDATER'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable version updater' }),
  enableOnboardingCheckout: z
    .boolean({
      error: (issue) =>
        issue.input === undefined
          ? 'Provide the variable NEXT_PUBLIC_ENABLE_ONBOARDING_CHECKOUT'
          : 'Not a boolean',
    })
    .meta({ description: 'Enable checkout on onboarding' }),
});

const featuresFlagConfig = FeatureFlagsSchema.parse({
  enableThemeToggle: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_THEME_TOGGLE,
    true,
  ),
  enableAccountDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION,
    false,
  ),
  enableTeamDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION,
    false,
  ),
  enableTeamAccounts: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS,
    true,
  ),
  enableTeamCreation: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION,
    true,
  ),
  enablePersonalAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING,
    false,
  ),
  enableTeamAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING,
    false,
  ),
  languagePriority: process.env
    .NEXT_PUBLIC_LANGUAGE_PRIORITY as LanguagePriority,
  enableNotifications: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_NOTIFICATIONS,
    true,
  ),
  realtimeNotifications: getBoolean(
    process.env.NEXT_PUBLIC_REALTIME_NOTIFICATIONS,
    false,
  ),
  enableVersionUpdater: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_VERSION_UPDATER,
    false,
  ),
  enableOnboardingCheckout: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_ONBOARDING_CHECKOUT,
    false,
  ),
} satisfies z.infer<typeof FeatureFlagsSchema>);

export default featuresFlagConfig;

function getBoolean(value: unknown, defaultValue: boolean) {
  if (typeof value === 'string') {
    return value === 'true';
  }

  return defaultValue;
}
